import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { changeUserStatus, getUsersWithRoles, setUserRoles } from '../api/usersApi'
import type { PagedResponse, UserStatus, UserWithRoles } from '../types'

const KEY = 'admin-users'

/**
 * Kullanıcı yönetimi sunucu-durumu: arama listesi (atanmış rollerle; opsiyonel rol filtresi) + rol atama
 * (set) + durum değişimi.
 */
export function useUsers(q: string, roleId?: number) {
  const qc = useQueryClient()
  const list = useQuery({ queryKey: [KEY, q, roleId], queryFn: () => getUsersWithRoles(q, roleId) })

  /**
   * Dönen satırı önbellekte ANINDA yerine koyar; `invalidate` tek başına yetmez çünkü refetch
   * tamamlanana kadar satır eski `modDate`'i taşır ve hızlı ikinci bir aç/kapa optimistic lock
   * hatası alır (rol yönetiminde aynı gerekçeyle aynı desen var).
   */
  const applyUpdated = (updated: UserWithRoles) =>
    qc.setQueryData<PagedResponse<UserWithRoles>>([KEY, q, roleId], (prev) =>
      prev
        ? { ...prev, content: prev.content.map((u) => (u.id === updated.id ? updated : u)) }
        : prev,
    )

  const saveRoles = useMutation({
    mutationFn: ({ id, roleIds }: { id: number; roleIds: number[] }) => setUserRoles(id, roleIds),
    // Kaydetme sonrası listeyi tazele → Roller sütunu güncellenir. Ayrıca oturumdaki kullanıcının efektif
    // yetkileri (/me) tazelenir → kendi rolleri değiştiyse buton/kolon görünürlüğü yenilemeye gerek kalmadan güncellenir.
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [KEY] })
      qc.invalidateQueries({ queryKey: ['me'] })
    },
  })

  const changeStatus = useMutation({
    mutationFn: ({ id, body }: { id: number; body: UserStatus }) => changeUserStatus(id, body),
    onSuccess: (data) => {
      applyUpdated(data)
      qc.invalidateQueries({ queryKey: [KEY] })
    },
  })

  return { list, saveRoles, changeStatus }
}
